import { useCallback, useEffect, useMemo, useRef } from 'react'
import { debounce } from 'lodash'
import { useApolloClient } from '@apollo/client'
import { isSuccess, useFirebase } from 'src/firebase'
import {
  RefreshParticipantIndicatorsDocument,
  RefreshParticipantIndicatorsQuery,
  RefreshParticipantIndicatorsQueryVariables,
} from './searchParticipants.generated'

// uid -> server timestamp of the last indicator-relevant change, written by the server's signalCareTeam
export type CoachSignals = Record<string, number>

const REFRESH_DEBOUNCE_MS = 500

// A coach who has never been signalled has no node at all, which reads the same as an empty map
const NO_SIGNALS: CoachSignals = {}

export function changedUIDs(previous: CoachSignals, next: CoachSignals): string[] {
  return Object.entries(next)
    .filter(([uid, timestamp]) => previous[uid] !== timestamp)
    .map(([uid]) => uid)
}

type Options = {
  coachID: string
  showPriorityIndicators: boolean
  enabled: boolean
}

/**
 * Refetches a participant's indicators whenever the server signals a change to them through
 * `subscriptions/participantIndicators/{coachID}`. The Apollo cache merges the refetched indicators into the participant list's rows.
 *
 * The first snapshot only sets the baseline: the list query already has those indicators. Firebase replays
 * changes missed while offline as one snapshot on reconnect, so those are refetched too.
 */
export default function useLiveParticipantIndicators({ coachID, showPriorityIndicators, enabled }: Options) {
  const client = useApolloClient()
  const signalsRequest = useFirebase<CoachSignals>(enabled ? `subscriptions/participantIndicators/${coachID}` : null)

  const showPriorityIndicatorsRef = useRef(showPriorityIndicators)
  showPriorityIndicatorsRef.current = showPriorityIndicators

  const pendingUIDs = useRef(new Set<string>())
  const refetchPending = useMemo(() => {
    const refetch = (uids: string[]) =>
      client.query<RefreshParticipantIndicatorsQuery, RefreshParticipantIndicatorsQueryVariables>({
        query: RefreshParticipantIndicatorsDocument,
        variables: { uids, showPriorityIndicators: showPriorityIndicatorsRef.current },
        fetchPolicy: 'network-only',
      })
    const warn = (uids: string[], error: unknown) =>
      console.warn('Failed to refresh participant indicators', { uids, error })

    return debounce(() => {
      const uids = [...pendingUIDs.current]
      pendingUIDs.current.clear()
      refetch(uids).catch((error) => {
        if (uids.length === 1) return warn(uids, error)
        // `patients(uids:)` is denied as a whole if the coach has lost any one of the participants (e.g. a
        // transfer while offline), so retry each one alone to still refresh the rest
        return uids.forEach((uid) => refetch([uid]).catch((retryError) => warn([uid], retryError)))
      })
    }, REFRESH_DEBOUNCE_MS)
  }, [client])
  useEffect(() => () => refetchPending.cancel(), [refetchPending])

  // Signals arriving within REFRESH_DEBOUNCE_MS of each other are refetched in one query
  const queueRefresh = useCallback(
    (uids: string[]) => {
      if (uids.length === 0) return
      uids.forEach((uid) => pendingUIDs.current.add(uid))
      refetchPending()
    },
    [refetchPending]
  )

  const signals = isSuccess(signalsRequest) ? signalsRequest.data ?? NO_SIGNALS : undefined
  const lastSeenSignals = useRef<CoachSignals | undefined>(undefined)
  useEffect(() => {
    if (signals === undefined) return
    const previous = lastSeenSignals.current
    lastSeenSignals.current = signals
    // The first snapshot is only the baseline: the list query has just loaded those indicators
    if (previous === undefined) return

    queueRefresh(changedUIDs(previous, signals))
  }, [signals, queueRefresh])
}
